<template>
  <div v-if="statusMappingsInitialized" class="entity-workflow-status-mapper">
    <div class="entity-workflow-status-mapper__header">
      <h5>Current Status</h5>
      <h5>New Status</h5>
    </div>
    <div class="entity-workflow-status-mapper__table">
      <div v-for="status in currentWorkflow.listStatuses" :key="status.id">
        <div class="status-holder">
          <status-chicklet :status="status" size="medium" />
        </div>
        <div class="status-holder">
          <status-picker
            v-model:selected-id="internalStatusMappings.find(m => m.oldStatusID === status.id).newStatusID" 
            :statuses="newWorkflowStatusesWithNullOption"
          />
        </div>
        <circle-arrow-icon />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import {
  IStatusForSelection,
  IWorkflowForSelection,
} from '@/api/types/response.types'
import { IStatusMapping } from '@/api/types/request.types'

/* API */

export default defineComponent({
  name: 'EntityWorkflowStatusMapper',

  props: {
    statusMappings: {
      type: Array as () => IStatusMapping[],
      required: true,
    },
    currentWorkflow: {
      type: Object as () => IWorkflowForSelection,
      required: true,
    },
    newWorkflow: {
      type: Object as () => IWorkflowForSelection,
      default: null,
    },
  },

  emits: ['update:statusMappings'],

  data() {
    return {
      statusMappingsInitialized: false,
      newWorkflowStatusesWithNullOption: [] as IStatusForSelection[],
    }
  },

  computed: {
    internalStatusMappings: {
      get(): IStatusMapping[] {
        return this.statusMappings
      },
      set(value: IStatusMapping[]) {
        this.$emit('update:statusMappings', value)
      },
    },
  },
  watch: {
    newWorkflow: {
      immediate: true,
      handler(val: IWorkflowForSelection) {
        this.statusMappingsInitialized = false
        const newMappings = [] as IStatusMapping[]

        this.newWorkflowStatusesWithNullOption = []
        this.newWorkflowStatusesWithNullOption.push({
          id: null,
          name: 'Select a status...',
          color: 'bluish-grey',
        })
        this.newWorkflowStatusesWithNullOption.push(...val.listStatuses)

        this.currentWorkflow.listStatuses.forEach(c => {
          const newStatusCandidate = this.newWorkflow.listStatuses.find(
            n => c.name.toLowerCase().trim() === n.name.toLowerCase().trim(),
          )
          const newStatusIDCandidate = newStatusCandidate
            ? newStatusCandidate.id
            : null

          newMappings.push({
            oldStatusID: c.id,
            newStatusID: newStatusIDCandidate,
          })
        })

        this.internalStatusMappings = newMappings
        this.$nextTick(() => {
          this.statusMappingsInitialized = true
        })
      },
    },
  },
})
</script>

<style lang="scss">
.entity-workflow-status-mapper {
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    position: relative;
    margin-top: 20px;

    > * {
      width: 50%;
      display: flex;
      justify-content: center;
    }
    .ddi-arrow-right {
      position: absolute;
      left: calc(50% - 6px);
      top: 0;
      color: var(--color-text-third);
    }
  }

  &__table {
    width: 100%;
    height: 250px;
    overflow-y: scroll;
    background-color: var(--color-background-secondary);
    border: $border--solid-light;
    border-radius: 8px;
    display: flex;
    flex-direction: column;

    /* Override popper height of status picker so it doesn't flow beyond the height of the table */
    .dropdown__popper {
      max-height: 230px !important;
    }

    /* Row */
    > div {
      width: 100%;
      display: flex;
      background-color: var(--color-background-header);
      border-bottom: $border--solid-light;
      position: relative;

      /* Cell */
      > div {
        width: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px;

        &.status-holder > * {
          max-width: 90%;
        }
        &:first-child {
          background-color: var(--color-background-primary);
          border-right: $border--solid-light;
        }
      }

      .circle-arrow-icon {
        position: absolute;
        left: calc(50% - 12px);
        top: 14px;
      }
    }
  }
}
</style>
